import { computed, Injectable, signal } from '@angular/core';

import { TimeTrackingRepository } from '../domain/time-tracking.repository';
import {
  ManualTimeEntryInput,
  StartTimerInput,
  TimeActivityTotal,
  TimeTaskTotal,
  TimeEntry,
  TimeTag,
  TimeTrackingSummary,
  UpdateTimeEntryInput
} from '../models/time-tracking.model';

const LONG_RUNNING_TIMER_SECONDS = 12 * 60 * 60;

@Injectable({ providedIn: 'root' })
export class TimeTrackingService {
  private readonly activeEntriesState = signal<TimeEntry[]>([]);

  readonly activeEntries = this.activeEntriesState.asReadonly();
  readonly activeCount = computed(() => this.activeEntriesState().length);

  constructor(private readonly repository: TimeTrackingRepository) {}

  getEntries(): Promise<TimeEntry[]> {
    return this.repository.getEntries();
  }

  async getActiveEntries(): Promise<TimeEntry[]> {
    const entries = await this.repository.getActiveEntries();
    this.activeEntriesState.set(entries);
    return entries;
  }

  refreshActiveEntries(): Promise<TimeEntry[]> {
    return this.getActiveEntries();
  }

  clearActiveEntries(): void {
    this.activeEntriesState.set([]);
  }

  getTags(): Promise<TimeTag[]> {
    return this.repository.getTags();
  }

  getActivityTagIds(activityId: number): Promise<number[]> {
    if (!Number.isSafeInteger(activityId) || activityId <= 0) return Promise.resolve([]);
    return this.repository.getActivityTagIds(activityId);
  }

  resolveTag(name: string): Promise<TimeTag> {
    const normalized = name.trim();
    if (!normalized || normalized.length > 60) {
      throw new Error('Un tag doit contenir entre 1 et 60 caractères.');
    }
    return this.repository.resolveTag(normalized);
  }

  setEntryTags(entryId: number, tagIds: number[]): Promise<void> {
    const uniqueIds = [...new Set(tagIds.filter(id => Number.isInteger(id) && id > 0))];
    return this.repository.setEntryTags(entryId, uniqueIds);
  }

  startTimer(input: StartTimerInput): Promise<TimeEntry> {
    const links = this.normalizeLinks(input.activityId, input.taskId);
    const startedAt = input.startedAt ?? new Date().toISOString();

    return this.repository.startTimer({
      ...links,
      startedAt,
      timezone: this.resolveTimezone(input.timezone),
      note: this.normalizeNote(input.note)
    }).then(entry => {
      this.upsertActiveEntry(entry);
      return entry;
    });
  }

  async stopTimer(entryId: number, endedAt = new Date().toISOString()): Promise<TimeEntry> {
    const entry = await this.repository.stopTimer(entryId, endedAt);
    this.removeActiveEntry(entryId);
    return entry;
  }

  async cancelTimer(entryId: number): Promise<void> {
    await this.repository.cancelTimer(entryId);
    this.removeActiveEntry(entryId);
  }

  createManualEntry(input: ManualTimeEntryInput): Promise<TimeEntry> {
    const links = this.normalizeLinks(input.activityId, input.taskId);
    this.assertChronology(input.startedAt, input.endedAt);

    return this.repository.createManualEntry({
      ...links,
      startedAt: input.startedAt,
      endedAt: input.endedAt,
      timezone: this.resolveTimezone(input.timezone),
      note: this.normalizeNote(input.note)
    });
  }

  updateEntry(entryId: number, input: UpdateTimeEntryInput): Promise<TimeEntry> {
    const links = this.normalizeLinks(input.activityId, input.taskId);
    if (input.endedAt !== null) {
      this.assertChronology(input.startedAt, input.endedAt);
    }

    return this.repository.updateEntry(entryId, {
      ...input,
      ...links,
      timezone: this.resolveTimezone(input.timezone),
      note: this.normalizeNote(input.note)
    }).then(entry => {
      this.syncActiveEntry(entry);
      return entry;
    });
  }

  updateEntryWithTags(
    entryId: number,
    input: UpdateTimeEntryInput,
    tagIds: number[]
  ): Promise<TimeEntry> {
    const links = this.normalizeLinks(input.activityId, input.taskId);
    if (input.endedAt !== null) {
      this.assertChronology(input.startedAt, input.endedAt);
    }

    const uniqueTagIds = [...new Set(tagIds.filter(id => Number.isInteger(id) && id > 0))];

    return this.repository.updateEntryWithTags(entryId, {
      ...input,
      ...links,
      timezone: this.resolveTimezone(input.timezone),
      note: this.normalizeNote(input.note)
    }, uniqueTagIds).then(entry => {
      this.syncActiveEntry(entry);
      return entry;
    });
  }

  async deleteEntry(entryId: number): Promise<void> {
    await this.repository.deleteEntry(entryId);
    this.removeActiveEntry(entryId);
  }

  async getSummary(date: string): Promise<TimeTrackingSummary> {
    const weekStart = this.startOfIsoWeek(date);
    const weekEnd = this.addDays(weekStart, 6);
    const slices = await this.repository.getDailySlices(weekStart, weekEnd);
    const todaySlices = slices.filter(slice => slice.localDate === date);

    return {
      date,
      weekStart,
      weekEnd,
      todaySeconds: todaySlices.reduce((sum, slice) => sum + slice.durationSeconds, 0),
      weekSeconds: slices.reduce((sum, slice) => sum + slice.durationSeconds, 0),
      todayByActivity: this.groupByActivity(todaySlices),
      weekByActivity: this.groupByActivity(slices),
      todayByTask: this.groupByTask(todaySlices),
      weekByTask: this.groupByTask(slices)
    };
  }

  elapsedSeconds(entry: TimeEntry, now = Date.now()): number {
    const start = Date.parse(entry.startedAt);
    const end = entry.endedAt ? Date.parse(entry.endedAt) : now;
    if (!Number.isFinite(start) || !Number.isFinite(end)) return 0;
    return Math.max(0, Math.floor((end - start) / 1000));
  }

  isLongRunning(entry: TimeEntry, now = Date.now()): boolean {
    return entry.endedAt === null && this.elapsedSeconds(entry, now) >= LONG_RUNNING_TIMER_SECONDS;
  }

  private syncActiveEntry(entry: TimeEntry): void {
    if (entry.endedAt === null) {
      this.upsertActiveEntry(entry);
    } else {
      this.removeActiveEntry(entry.id);
    }
  }

  private upsertActiveEntry(entry: TimeEntry): void {
    this.activeEntriesState.update(entries => {
      const withoutEntry = entries.filter(existing => existing.id !== entry.id);
      return [...withoutEntry, entry];
    });
  }

  private removeActiveEntry(entryId: number): void {
    this.activeEntriesState.update(entries => entries.filter(entry => entry.id !== entryId));
  }

  private normalizeLinks(
    activityId: number | null | undefined,
    taskId: number | null | undefined
  ): { activityId: number | null; taskId: number | null } {
    const normalized = {
      activityId: activityId ?? null,
      taskId: taskId ?? null
    };

    if (normalized.activityId === null && normalized.taskId === null) {
      throw new Error('Une session doit être liée à une Activity ou à une Task.');
    }

    if (normalized.activityId !== null && normalized.taskId !== null) {
      throw new Error('Une session ne peut pas être liée à la fois à une Activity et à une Task.');
    }

    return normalized;
  }

  private normalizeNote(note: string | null | undefined): string | null {
    const value = note?.trim() ?? '';
    return value || null;
  }

  private resolveTimezone(timezone?: string): string {
    const explicit = timezone?.trim();
    if (explicit) return explicit;

    const detected = typeof Intl !== 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : '';
    return detected || 'UTC';
  }

  private assertChronology(startedAt: string, endedAt: string): void {
    const start = Date.parse(startedAt);
    const end = Date.parse(endedAt);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
      throw new Error('La fin d’une session doit être postérieure à son début.');
    }
  }

  private startOfIsoWeek(date: string): string {
    const parsed = this.parseDateOnly(date);
    const isoDay = parsed.getUTCDay() || 7;
    parsed.setUTCDate(parsed.getUTCDate() - (isoDay - 1));
    return this.formatDateOnly(parsed);
  }

  private addDays(date: string, days: number): string {
    const parsed = this.parseDateOnly(date);
    parsed.setUTCDate(parsed.getUTCDate() + days);
    return this.formatDateOnly(parsed);
  }

  private parseDateOnly(date: string): Date {
    const [year, month, day] = date.split('-').map(Number);
    const parsed = new Date(Date.UTC(year, month - 1, day));
    if (
      !Number.isInteger(year)
      || !Number.isInteger(month)
      || !Number.isInteger(day)
      || Number.isNaN(parsed.getTime())
    ) {
      throw new Error('Date invalide.');
    }
    return parsed;
  }

  private formatDateOnly(date: Date): string {
    return date.toISOString().slice(0, 10);
  }

  private groupByActivity(
    slices: Array<{ activityId: number | null; durationSeconds: number }>
  ): TimeActivityTotal[] {
    const totals = new Map<number | null, number>();

    for (const slice of slices) {
      totals.set(
        slice.activityId,
        (totals.get(slice.activityId) ?? 0) + slice.durationSeconds
      );
    }

    return [...totals.entries()]
      .map(([activityId, seconds]) => ({ activityId, seconds }))
      .sort((left, right) => right.seconds - left.seconds);
  }

  private groupByTask(
    slices: Array<{ taskId: number | null; durationSeconds: number }>
  ): TimeTaskTotal[] {
    const totals = new Map<number | null, number>();

    for (const slice of slices) {
      totals.set(
        slice.taskId,
        (totals.get(slice.taskId) ?? 0) + slice.durationSeconds
      );
    }

    return [...totals.entries()]
      .map(([taskId, seconds]) => ({ taskId, seconds }))
      .sort((left, right) => right.seconds - left.seconds);
  }
}
